import { useMutation, useQueryClient } from '@tanstack/react-query'

import type {
  PatchExtensionWebhookConfigRequest,
  WebhookConfigDetail,
} from '@mangovoice/types'

import type { ApiError } from '../api/types'
import type { WebhooksApi } from '../api/webhooksApi'

interface UsePatchExtensionIntegrationParams {
  api: WebhooksApi
  pbxId: number
  integrationId: number
}

interface MutationVariables {
  extId: number
  data: PatchExtensionWebhookConfigRequest
}

export const usePatchExtensionIntegration = ({
  api,
  pbxId,
  integrationId,
}: UsePatchExtensionIntegrationParams) => {
  const queryClient = useQueryClient()
  const queryKey = ['webhook', pbxId, integrationId]

  return useMutation<unknown, ApiError, MutationVariables>({
    mutationFn: async ({ extId, data }) => {
      const response = await api.patchExtensionWebhook(
        integrationId,
        extId,
        data,
      )

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data
    },
    onMutate: async ({ extId, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const snapshot = queryClient.getQueryData<WebhookConfigDetail>(queryKey)

      if (snapshot && data.active !== undefined) {
        queryClient.setQueryData<WebhookConfigDetail>(queryKey, {
          ...snapshot,
          extensions: snapshot.extensions.map((ext) =>
            ext.ext_id === extId ? { ...ext, active: data.active! } : ext,
          ),
        })
      }

      return { snapshot }
    },
    onError: (_error, _vars, context) => {
      const { snapshot } = context as {
        snapshot: WebhookConfigDetail | undefined
      }
      if (snapshot) {
        queryClient.setQueryData(queryKey, snapshot)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}
